import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';

interface TrendCardProps {
  title: string;
  initialScore?: number;
  previousScore?: number;
  currentScore?: number;
  maxScore: number;
}

const TrendCard: React.FC<TrendCardProps> = ({
  title,
  initialScore,
  previousScore,
  currentScore,
  maxScore
}) => {
  const getTrendIndicator = (current: number | undefined, previous: number | undefined) => {
    if (current === undefined || previous === undefined) {
      return { icon: <TrendingFlatIcon />, color: '#9e9e9e', text: 'N/A', bgColor: 'rgba(158, 158, 158, 0.1)' };
    }
    
    // Lower scores = improvement for PHQ-9 and GAD-7
    const change = previous - current;
    
    if (change >= 5) {
      return { 
        icon: <TrendingUpIcon />, 
        color: '#4caf50', 
        text: 'Improving',
        bgColor: 'rgba(76, 175, 80, 0.1)'
      };
    }
    if (change <= -5) {
      return { 
        icon: <TrendingDownIcon />, 
        color: '#f44336', 
        text: 'Worsening',
        bgColor: 'rgba(244, 67, 54, 0.1)'
      };
    }
    return { 
      icon: <TrendingFlatIcon />, 
      color: '#ff9800', 
      text: 'Stable',
      bgColor: 'rgba(255, 152, 0, 0.1)'
    };
  };

  const trend = getTrendIndicator(currentScore, previousScore);

  return (
    <Box 
      sx={{ 
        p: 3, 
        bgcolor: 'rgba(255, 255, 255, 0.05)', 
        borderRadius: 2,
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}
    >
      <Typography variant="subtitle2" className="theme-text-primary" sx={{ mb: 2, fontWeight: 600 }}>
        {title}
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        {initialScore !== undefined && (
          <Box>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>
              Initial
            </Typography>
            <Typography variant="h5" className="theme-text-primary" sx={{ fontWeight: 600 }}>
              {initialScore}<Typography component="span" variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>/{maxScore}</Typography>
            </Typography>
          </Box>
        )}
        
        {previousScore !== undefined && (
          <Box>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>
              Previous
            </Typography>
            <Typography variant="h5" className="theme-text-primary" sx={{ fontWeight: 600 }}>
              {previousScore}<Typography component="span" variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>/{maxScore}</Typography>
            </Typography>
          </Box>
        )}
        
        {currentScore !== undefined && (
          <Box>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>
              Current
            </Typography>
            <Typography variant="h5" className="theme-text-primary" sx={{ fontWeight: 600 }}>
              {currentScore}<Typography component="span" variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>/{maxScore}</Typography>
            </Typography>
          </Box>
        )}
        
        {previousScore !== undefined && currentScore !== undefined && (
          <Chip 
            icon={trend.icon}
            label={trend.text}
            sx={{
              bgcolor: trend.bgColor,
              color: trend.color,
              fontWeight: 600,
              border: `1px solid ${trend.color}`,
              '& .MuiChip-icon': {
                color: trend.color
              }
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default TrendCard;
